import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import * as accountApi from '../api/accountApi.js';
import * as authApi from '../../auth/api/authApi.js';
import {
  accountRequested, accountLoadFinished, nativeAccountOpened,
  profileRequested, profileLoaded, profileLoadFailed,
  profileSaveRequested, profileFieldSaveRequested, profileSaved, profileSaveFailed,
  passwordResetRequested, passwordResetSent, passwordResetFailed,
  notifyPrefsRequested, notifyPrefsLoaded, notifyPrefsLoadFailed,
  notifyPrefsChanged, notifyPrefsSaved, notifyPrefsSaveFailed,
  speechPrefsRequested, speechPrefsLoaded, speechPrefsLoadFailed,
  speechPrefsChanged, speechPrefsSaved, speechPrefsSaveFailed,
  ordersRequested, ordersLoaded, ordersLoadFailed,
  certificatesRequested, certificatesLoaded, certificatesLoadFailed,
  licensesLoaded, licensesLoadFailed,
  phoneChangeStarted,
  phoneOtpSent,
  phoneOtpRequestFailed,
  phoneOtpVerifyRequested,
  phoneOtpVerifyFailed,
  phoneOtpVerifySucceeded,
  phoneChangeSucceeded,
  phoneChangeFailed,
} from './accountSlice.js';
import { toastShown } from '../../system/store/toastSlice.js';
import { friendlyErrorMessage } from '../../../lib/errorCopy.js';
import { captureSagaError } from '../../../lib/sentry.js';
import { authUserPatched } from '../../auth/store/authSlice.js';
import {
  getResetPinCooldownRemaining,
  startResetPinCooldown,
} from '../lib/resetPinCooldown.js';

// Wraps a thunk so all([...]) doesn't abort siblings when one rejects.
// Same pattern as features/library/store/librarySaga.js's safeCall.
function* safeCall(fn) {
  try {
    const value = yield call(fn);
    return { value, error: null };
  } catch (error) {
    return { value: null, error };
  }
}

function isOk(res) {
  return res && res.status && res.code === 200;
}

function authIdentityPatch(profile = {}) {
  return Object.fromEntries(Object.entries({
    full_name: profile.full_name,
    display_name: profile.display_name,
    email: profile.email,
    user_name: profile.user_name ?? profile.email,
    phone: profile.phone,
    onboarding: profile.onboarding,
  }).filter(([, value]) => value !== undefined));
}

function profileMutationErrorMessage(err, fallback = 'Could not update profile.') {
  return err?.response?.data?.message
    || err?.response?.data?.error
    || err?.message
    || fallback;
}

export function* handleAccountRequested() {
  const [profileRes, notifyRes, speechRes, ordersRes, certsRes, licensesRes] = yield all([
    call(safeCall, accountApi.fetchProfile),
    call(safeCall, accountApi.fetchNotifyPrefs),
    call(safeCall, accountApi.fetchSpeechPrefs),
    call(safeCall, accountApi.fetchOrders),
    call(safeCall, accountApi.fetchCertificates),
    call(safeCall, accountApi.fetchLicenses),
  ]);

  // Profile
  if (profileRes.error) {
    yield put(profileLoadFailed(profileRes.error.message ?? 'Network error'));
  } else if (!isOk(profileRes.value)) {
    yield put(profileLoadFailed(profileRes.value?.message ?? 'Failed to load profile'));
  } else {
    yield put(profileLoaded(profileRes.value.data));
    yield put(authUserPatched(authIdentityPatch(profileRes.value.data)));
  }

  // Notify prefs
  if (notifyRes.error) {
    yield put(notifyPrefsLoadFailed(notifyRes.error.message ?? 'Network error'));
  } else if (!isOk(notifyRes.value)) {
    yield put(notifyPrefsLoadFailed(notifyRes.value?.message ?? 'Failed to load preferences'));
  } else {
    yield put(notifyPrefsLoaded(notifyRes.value.data));
  }

  // Speech prefs
  if (speechRes.error) {
    yield put(speechPrefsLoadFailed(speechRes.error.message ?? 'Network error'));
  } else if (!isOk(speechRes.value)) {
    yield put(speechPrefsLoadFailed(speechRes.value?.message ?? 'Failed to load microphone preference'));
  } else {
    yield put(speechPrefsLoaded(speechRes.value.data));
  }

  // Orders
  if (ordersRes.error) {
    yield put(ordersLoadFailed(ordersRes.error.message ?? 'Network error'));
  } else if (!isOk(ordersRes.value)) {
    yield put(ordersLoadFailed(ordersRes.value?.message ?? 'Failed to load orders'));
  } else {
    yield put(ordersLoaded(ordersRes.value.data));
  }

  // Certificates
  if (certsRes.error) {
    yield put(certificatesLoadFailed(certsRes.error.message ?? 'Network error'));
  } else if (!isOk(certsRes.value)) {
    yield put(certificatesLoadFailed(certsRes.value?.message ?? 'Failed to load certificates'));
  } else {
    yield put(certificatesLoaded(certsRes.value.data));
  }

  // Licenses — failure is common for B2C users; treat any non-success as "no org info."
  if (licensesRes.error || !isOk(licensesRes.value)) {
    yield put(licensesLoadFailed(licensesRes.error?.message ?? licensesRes.value?.message ?? 'No license info'));
  } else {
    yield put(licensesLoaded(licensesRes.value.data));
  }

  // Mark the session cache usable even when individual sections failed. Those
  // sections retain their own failed state and can be retried explicitly.
  yield put(accountLoadFinished());
}

export function* handleNativeAccountOpened() {
  const loadStatus = yield select((state) => state.account?.accountLoadStatus ?? 'idle');
  if (loadStatus !== 'idle') return;
  yield put(accountRequested());
}

export function* handleOrdersRequested() {
  try {
    const response = yield call(accountApi.fetchOrders);
    if (!isOk(response)) {
      yield put(ordersLoadFailed(response?.message ?? 'Failed to load orders'));
      return;
    }
    yield put(ordersLoaded(response.data));
  } catch (error) {
    yield put(ordersLoadFailed(error?.message ?? 'Network error'));
  }
}

export function* handleProfileRequested() {
  try {
    const response = yield call(accountApi.fetchProfile);
    if (!isOk(response)) {
      yield put(profileLoadFailed(response?.message ?? 'Failed to load profile'));
      return;
    }
    yield put(profileLoaded(response.data));
    yield put(authUserPatched(authIdentityPatch(response.data)));
  } catch (error) {
    yield put(profileLoadFailed(error?.message ?? 'Network error'));
  }
}

export function* handleNotifyPrefsRequested() {
  try {
    const response = yield call(accountApi.fetchNotifyPrefs);
    if (!isOk(response)) {
      yield put(notifyPrefsLoadFailed(response?.message ?? 'Failed to load preferences'));
      return;
    }
    yield put(notifyPrefsLoaded(response.data));
  } catch (error) {
    yield put(notifyPrefsLoadFailed(error?.message ?? 'Network error'));
  }
}

export function* handleSpeechPrefsRequested() {
  try {
    const response = yield call(accountApi.fetchSpeechPrefs);
    if (!isOk(response)) {
      yield put(speechPrefsLoadFailed(response?.message ?? 'Failed to load microphone preference'));
      return;
    }
    yield put(speechPrefsLoaded(response.data));
  } catch (error) {
    yield put(speechPrefsLoadFailed(error?.message ?? 'Network error'));
  }
}

export function* handleCertificatesRequested() {
  try {
    const response = yield call(accountApi.fetchCertificates);
    if (!isOk(response)) {
      yield put(certificatesLoadFailed(response?.message ?? 'Failed to load certificates'));
      return;
    }
    yield put(certificatesLoaded(response.data));
  } catch (error) {
    yield put(certificatesLoadFailed(error?.message ?? 'Network error'));
  }
}

export function* handleProfileSaveRequested() {
  const { profile, profileDraft } = yield select((s) => s.account);
  // Build the full POST body: current profile + draft overrides.
  // Legacy expects '1'/'0' strings for booleans — caller passes them as strings.
  const body = { ...profile, ...profileDraft };
  try {
    const res = yield call(accountApi.updateProfile, body);
    if (!isOk(res)) {
      yield put(profileSaveFailed(res?.message ?? 'Save failed'));
      return;
    }
    const savedProfile = accountApi.normalizeProfile(accountApi.serializeProfileBody(body));
    yield put(profileSaved(savedProfile));
    yield put(authUserPatched(authIdentityPatch(savedProfile)));
    yield put(toastShown({ variant: 'success', message: 'Profile updated' }));
  } catch (err) {
    yield put(profileSaveFailed(profileMutationErrorMessage(err, 'Save failed')));
  }
}

export function* handleProfileFieldSaveRequested(action) {
  const { profile } = yield select((s) => s.account);
  const authUser = yield select((s) => s.auth?.user);
  // Build the body from current profile + the one field override. Don't touch profileDraft.
  const body = {
    ...(authUser ?? {}),
    ...(profile ?? {}),
    [action.payload.field]: action.payload.value,
  };
  try {
    const res = yield call(accountApi.updateProfile, body);
    if (!isOk(res)) {
      yield put(profileSaveFailed(res?.message ?? 'Save failed'));
      return;
    }
    const savedProfile = accountApi.normalizeProfile(accountApi.serializeProfileBody(body));
    yield put(profileSaved(savedProfile));
    yield put(authUserPatched(authIdentityPatch(savedProfile)));
  } catch (err) {
    yield put(profileSaveFailed(profileMutationErrorMessage(err, 'Save failed')));
  }
}

export function* handlePasswordResetRequested() {
  if (getResetPinCooldownRemaining() > 0) {
    yield put(passwordResetSent());
    return;
  }

  try {
    const res = yield call(accountApi.requestPinReset);
    if (!isOk(res)) {
      yield put(passwordResetFailed(res?.message ?? 'Could not send reset PIN'));
      return;
    }
    startResetPinCooldown();
    yield put(passwordResetSent());
  } catch (err) {
    yield put(passwordResetFailed(err?.message ?? 'Could not send reset PIN'));
  }
}

export function* handleNotifyPrefsChanged(action) {
  // The slice has already applied the optimistic update by the time this runs.
  // Read the *current* (post-optimistic) value; the pre-toggle value is
  // reconstructed via inversion below for the revert payload.
  const current = yield select((s) => s.account.notifyPrefs);
  const next = { ...current, ...action.payload };
  try {
    const res = yield call(accountApi.updateNotifyPrefs, next.notify_is_on);
    if (!isOk(res)) {
      yield put(notifyPrefsSaveFailed({
        previous: { ...current, ...{ notify_is_on: !next.notify_is_on } },
        error: res?.message ?? 'Save failed',
      }));
      return;
    }
    yield put(notifyPrefsSaved());
  } catch (err) {
    yield put(notifyPrefsSaveFailed({
      previous: { ...current, ...{ notify_is_on: !next.notify_is_on } },
      error: err?.message ?? 'Save failed',
    }));
  }
}

export function* handleSpeechPrefsChanged(action) {
  const current = yield select((s) => s.account.speechPrefs);
  const next = { ...current, ...action.payload };
  try {
    const res = yield call(accountApi.updateSpeechPrefs, next.speech_is_on);
    if (!isOk(res)) {
      yield put(speechPrefsSaveFailed({
        previous: { ...current, speech_is_on: !next.speech_is_on },
        error: res?.message ?? 'Save failed',
      }));
      return;
    }
    yield put(speechPrefsSaved());
    yield put(toastShown({
      variant: 'success',
      message: next.speech_is_on ? 'Microphone activated.' : 'Microphone deactivated.',
    }));
  } catch (err) {
    yield put(speechPrefsSaveFailed({
      previous: { ...current, speech_is_on: !next.speech_is_on },
      error: err?.message ?? 'Save failed',
    }));
  }
}

export function* handlePhoneChangeStarted(action) {
  const { profile } = yield select((s) => s.account);
  const body = {
    ...(profile ?? {}),
    ...(action.payload.draft ?? {}),
    phone: action.payload.draftPhone,
    whatsappNumber: action.payload.draft?.whatsappNumber || action.payload.draftPhone,
  };
  try {
    const res = yield call(accountApi.requestPhoneChangeOtp, body);
    if (res?.status && res.code === 200) {
      yield put(phoneOtpSent());
    } else {
      const message = res?.message || 'Could not send code.';
      yield put(phoneOtpRequestFailed(message));
      yield put(profileSaveFailed(message));
    }
  } catch (err) {
    captureSagaError(err, { action: action.type });
    const message = profileMutationErrorMessage(err, friendlyErrorMessage(err));
    yield put(phoneOtpRequestFailed(message));
    yield put(profileSaveFailed(message));
  }
}

export function* handlePhoneOtpVerifyRequested(action) {
  const phoneChange = yield select((s) => s.account?.phoneChange);
  if (!phoneChange) return;
  try {
    const res = yield call(authApi.verifyOtp, {
      login_id: phoneChange.draftPhone,
      otp: action.payload.otp,
    });
    if (!(res?.status && res.code === 200)) {
      yield put(phoneOtpVerifyFailed(res?.message || "That code didn't match."));
      return;
    }
    yield put(phoneOtpVerifySucceeded());
    // Re-check phoneChange in case the user cancelled (Escape / scrim / X / Cancel
    // button) while the verifyOtp request was in flight. `takeLatest` only
    // cancels on a NEW phoneOtpVerifyRequested, not on phoneChangeCancelled —
    // so we must bail here ourselves before committing the profile update.
    const stillActive = yield select((s) => s.account?.phoneChange);
    if (!stillActive) return;
    const merged = { ...(stillActive.draft ?? {}), phone: stillActive.draftPhone };
    try {
      yield call(accountApi.updateProfile, merged);
      const savedProfile = accountApi.normalizeProfile(accountApi.serializeProfileBody(merged));
      yield put(phoneChangeSucceeded(savedProfile));
      yield put(authUserPatched(authIdentityPatch(savedProfile)));
      yield put(toastShown({ variant: 'success', message: 'Phone updated.' }));
    } catch (commitErr) {
      captureSagaError(commitErr, { action: 'phoneChange/commit' });
      yield put(phoneChangeFailed(friendlyErrorMessage(commitErr)));
    }
  } catch (err) {
    captureSagaError(err, { action: action.type });
    yield put(phoneOtpVerifyFailed(friendlyErrorMessage(err)));
  }
}

export default function* accountSaga() {
  yield takeLatest(accountRequested.type, handleAccountRequested);
  yield takeLatest(nativeAccountOpened.type, handleNativeAccountOpened);
  yield takeLatest(profileRequested.type, handleProfileRequested);
  yield takeLatest(notifyPrefsRequested.type, handleNotifyPrefsRequested);
  yield takeLatest(speechPrefsRequested.type, handleSpeechPrefsRequested);
  yield takeLatest(ordersRequested.type, handleOrdersRequested);
  yield takeLatest(certificatesRequested.type, handleCertificatesRequested);
  yield takeLatest(profileSaveRequested.type, handleProfileSaveRequested);
  yield takeLatest(profileFieldSaveRequested.type, handleProfileFieldSaveRequested);
  yield takeLatest(passwordResetRequested.type, handlePasswordResetRequested);
  yield takeLatest(notifyPrefsChanged.type, handleNotifyPrefsChanged);
  yield takeLatest(speechPrefsChanged.type, handleSpeechPrefsChanged);
  yield takeLatest(phoneChangeStarted.type, handlePhoneChangeStarted);
  yield takeLatest(phoneOtpVerifyRequested.type, handlePhoneOtpVerifyRequested);
}
